import { Injectable } from '@angular/core';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { User as UserModel } from '@xpo-ltl/sdk-common';
import _ from 'lodash';
import { BehaviorSubject, interval } from 'rxjs';
import { ConfigManagerProperties } from '../../enums/config-manager-properties.enum';
import { UserRole } from '../../enums/user-role/user-role.enum';
import { RoleConfig } from '../../models/role-config.model';
import { UserRolesModel } from '@shared/models/shared/user.model';
import { filter, take, takeWhile, tap } from 'rxjs/operators';
import { RegexRules } from '@shared/utils/regex/regex.utils';
import { UserHandler } from '@app-store/user/handler/user.handler';

@Injectable({ providedIn: 'root' })
export class UserRoleService {
  userSource = new BehaviorSubject<UserModel>(undefined);
  user$ = this.userSource.asObservable();
  loggedUserRoles$ = new BehaviorSubject<UserRolesModel | undefined>(undefined);
  
  private isProd: boolean;
  private roleConfigValue: RoleConfig;

  get User(): UserModel {
    return this.userSource.value;
  }

  set User(value: UserModel) {
    this.userSource.next(value);
  }

  get isProduction(): boolean {
    return this.isProd;
  }

  get roleConfig(): RoleConfig {
    return this.roleConfigValue;
  }

  constructor(
    private configManagerService: ConfigManagerService,
    private userHandler: UserHandler,
  ) {
    this.runInitMethods();
  }

  private runInitMethods(): void {
    interval(0).pipe(
      takeWhile(() => this.isProdEmpty()),
      tap(() => this.setIsProd()),
      filter(() => !this.isProdEmpty())
    ).subscribe(
      () => {
        this.setRamRolesToCheck();
        this.setRamRoles();
      }
    );
  }

  private setRamRoles(): void {
    /** TODO: establish roles for PROD / NON-PROD */
    this.roleConfigValue = {
      pricingAdmin: UserRole.pricingAdmin,
      pricingCorr: UserRole.pricingCorr,
      pricingOps: UserRole.pricingOps,
      ramAccessorialMgmtCorr: UserRole.ramAccessorialMgmtCorr,
      ramAccessorialMgmtOps: UserRole.ramAccessorialMgmtOps,
    };
    if (!this.isProd) {
      this.roleConfigValue.ramAccessorialMgmtCorrTest = UserRole.ramAccessorialMgmtCorrTest;
      this.roleConfigValue.ramAccessorialMgmtOpsTest = UserRole.ramAccessorialMgmtOpsTest;
    }
  }

  private setRamRolesToCheck(): void {
    this.userHandler.getLoggedUser$.pipe(
      filter(user => !!user),
      take(1)
    ).subscribe(
      loggedUser => {
        const loggedUserRoles = loggedUser?.roles;
        if (!loggedUserRoles) return;

        let userRoles: UserRolesModel = {};
        Object.entries(loggedUserRoles).map(
          ([key, value]) => {
            key = key.replace(RegexRules.getTextBeforeSlash, '');
            value = value.replace(RegexRules.getTextBeforeSlash, '');    
            userRoles[key] = value;
          }
        );
        
        this.loggedUserRoles$.next(userRoles);
      }
    );
  }

  private doesAnyRoleMatch(ramRolesToCheck: string[], loggedUserRoles: UserRolesModel | undefined): boolean {
    return loggedUserRoles
      ? ramRolesToCheck.some(ramRole => Object.values(loggedUserRoles).includes(ramRole))
      : false;
  }

  private isProdEmpty(): boolean {
    return this.isProd == undefined;
  }

  private setIsProd(): void {
    this.isProd = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production)
  }

  getLoggedUserRolesValue(): UserRolesModel | undefined {
    return this.loggedUserRoles$.value;
  }

  checkIfAnyRoleMatch(keyRamRoles: string[]): boolean {

    return this.doesAnyRoleMatch(keyRamRoles, this.getLoggedUserRolesValue());
  }
}
